import { PAGES } from 'context/pageContext';

export type PaymentMethod = 'TRANSFER' | 'CARD' | 'OPAY';

/** What the customer pays for one provider / payment method, calculated by the backend at initiation. All minor units. */
export interface IFeeQuote {
  provider: string;
  paymentMethod: PaymentMethod;
  baseAmount: number;
  fee: number;
  totalAmount: number;
  currency?: string;
}

export interface IPayableAmount {
  baseAmount: number;
  /** Our fee as quoted by the backend. */
  fee: number;
  /** What we charge: base amount + our fee. This is the amount sent to the provider. */
  totalAmount: number;
  /** The provider's own processing fee, which it adds on top when it charges the customer (see providerFee). */
  providerFee: number;
  /** Our fee and the provider's, shown to the customer as one fee. */
  displayFee: number;
  /** What the customer is actually debited: totalAmount + providerFee. Display only, never sent. */
  displayTotal: number;
}

// Pay with Transfer is our own virtual account (quoted under DEFAULT), except on FirstChekout, which runs its own
// transfer (see PaymentHandlerFactory.transfer)
const VIRTUAL_ACCOUNT_PROVIDER = 'DEFAULT';
const FIRSTCHEKOUT_PROVIDER = 'FIRSTCHEKOUT';
const OPAY_PROVIDER = 'OPAY';

const quotedProvider = (method: PaymentMethod, selectedProvider?: string) => {
  if (method === 'OPAY') return OPAY_PROVIDER;
  if (method === 'TRANSFER') {
    return selectedProvider === FIRSTCHEKOUT_PROVIDER
      ? FIRSTCHEKOUT_PROVIDER
      : VIRTUAL_ACCOUNT_PROVIDER;
  }
  return selectedProvider;
};

export const findFeeQuote = (
  quotes: IFeeQuote[] | undefined,
  method: PaymentMethod,
  selectedProvider?: string,
): IFeeQuote | undefined => {
  if (!quotes) return undefined;

  const provider = quotedProvider(method, selectedProvider);

  return quotes.find(
    (quote) => quote.paymentMethod === method && quote.provider === provider,
  );
};

// Paystack (cards) and OPay add their processing fee on top of what we charge: a percentage capped at a fixed
// amount. Keyed by method and provider; everything else adds nothing. grossUp: whether the provider takes its
// percentage of the whole amount the customer pays (fee included) rather than of what we charge.
const PROVIDER_FEES: Record<
  string,
  { basisPoints: number; cap: number; grossUp: boolean }
> = {
  'CARD:PAYSTACK': { basisPoints: 150, cap: 200000, grossUp: true }, // 1.5%, capped at 2,000 naira
  // 0.7% of what we charge, capped at 700 naira: OPay's checkout showed 6,902.99 for a 6,855.00 charge (+47.99)
  [`OPAY:${OPAY_PROVIDER}`]: { basisPoints: 70, cap: 70000, grossUp: false },
};

/**
 * The provider's fee on top of `amount` (minor units), rounded up to a whole kobo and never more than its cap.
 * A grossed-up provider takes its percentage of everything the customer pays, this fee included, so its fee is
 * amount × rate / (1 − rate); the others take amount × rate.
 */
export const providerFee = (
  amount: number,
  method?: PaymentMethod,
  selectedProvider?: string,
): number => {
  if (!method) return 0;
  const rate = PROVIDER_FEES[`${method}:${quotedProvider(method, selectedProvider)}`];
  if (!rate) return 0;

  const divisor = rate.grossUp ? 10000 - rate.basisPoints : 10000;
  const fee = Math.ceil((amount * rate.basisPoints) / divisor);
  return Math.min(fee, rate.cap);
};

/** Without a quote (older backend, or no option chosen yet) we charge the base amount, as before. */
export const resolvePayableAmount = (
  baseAmount: number,
  quote?: IFeeQuote,
  method?: PaymentMethod,
  selectedProvider?: string,
): IPayableAmount => {
  const fee = quote?.fee ?? 0;
  const totalAmount = quote?.totalAmount ?? baseAmount;
  const provider = providerFee(totalAmount, method, selectedProvider);

  return {
    baseAmount,
    fee,
    totalAmount,
    providerFee: provider,
    displayFee: fee + provider,
    displayTotal: totalAmount + provider,
  };
};

export const methodForPage = (page: PAGES): PaymentMethod | undefined => {
  switch (page) {
    case PAGES.CARD:
      return 'CARD';
    case PAGES.TRANSFER:
      return 'TRANSFER';
    case PAGES.OPAY:
      return 'OPAY';
    default:
      return undefined;
  }
};
